import { defineStore } from "pinia";
import {
    FetchState,
    InstrumentPerformanceData,
    InstrumentPerformanceDataState,
    InstrumentPerformanceGraphTimePeriod
} from "@/shared/types";
import Vue from "vue";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import moment from "moment";
import { useNotifierStore } from "./notifier";
import * as drivewealthService from "@/shared/api/drivewealthService";

export const initialState: InstrumentPerformanceDataState = {
    instrumentData: {}
};

export const useInstrumentPerformanceDataStore = defineStore("instrumentPerformanceData", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("instrumentPerformanceData"),
    },
    state: (): InstrumentPerformanceDataState => (_.cloneDeep(initialState)),
    getters: {
        instrumentPerformanceData (): (stockSymbol: string) => InstrumentPerformanceData {
            return stockSymbol => {
                return this.instrumentData[stockSymbol];
            };
        },
    },
    actions: {
        setInstrumentPerformanceFetchState ({ stockSymbol, fetchState }: {
            stockSymbol: string,
            fetchState: FetchState
        }) {
            Vue.set(this.instrumentData, stockSymbol, { stockSymbol, fetchState });
        },

        setInstrumentPerformanceData ({ stockSymbol, data }: {
            stockSymbol: string,
            data: InstrumentPerformanceData
        }) {
            Vue.set(this.instrumentData, stockSymbol, {...data});
        },
        async getInstrumentPerformanceData (stockSymbol: string): Promise<void> {
            if (this.instrumentPerformanceData[stockSymbol]) {
                return;
            }
            this.setInstrumentPerformanceFetchState({ stockSymbol, fetchState: FetchState.Loading });
            const notifierStore = useNotifierStore();
            return drivewealthService.getEmployeeInvestmentTypes()
                .then(response => {
                    const testData: InstrumentPerformanceData = {
                        fetchState: FetchState.Loaded,
                        stockSymbol,
                        companyName: `The Company Name INC`,
                        dataSource: "NASDAQ",
                        closeDateTime: moment().format("MM/DD/YYYY"),
                        currentPrice: 119.54,
                        todayClosePrice: 99.99,
                        todayOpenPrice: 129.99,
                        previousClose: 89.99,
                        graphDataPoints: {}
                    };

                    const graphTimePeriodKeys = Object.values(InstrumentPerformanceGraphTimePeriod);
                    for (const key in graphTimePeriodKeys) {
                        const enumValue = graphTimePeriodKeys[key];
                        testData.graphDataPoints[enumValue] = [ [ "Time", "Amount" ] ];
                        switch (enumValue) {
                            case InstrumentPerformanceGraphTimePeriod.Days1:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().subtract(8, "hours").toDate(), 15.99],
                                    [moment().subtract(6, "hours").toDate(), 31.99],
                                    [moment().subtract(4, "hours").toDate(), 16.99],
                                    [moment().subtract(2, "hours").toDate(), 23.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Days5:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-4, "days").toDate(), 11.99],
                                    [moment().add(-3, "days").toDate(), 27.99],
                                    [moment().add(-2, "days").toDate(), 13.99],
                                    [moment().add(-1, "days").toDate(), 26.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Months1:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-4, "weeks").toDate(), 17.99],
                                    [moment().add(-3, "weeks").toDate(), 21.99],
                                    [moment().add(-2, "weeks").toDate(), 15.99],
                                    [moment().add(-1, "weeks").toDate(), 28.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Months3:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-2, "months").toDate(), 17.99],
                                    [moment().add(-1, "months").toDate(), 24.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Months6:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-4, "months").toDate(), 17.99],
                                    [moment().add(-3, "months").toDate(), 29.99],
                                    [moment().add(-2, "months").toDate(), 16.99],
                                    [moment().add(-1, "months").toDate(), 21.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Years1:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-10, "months").toDate(), 17.99],
                                    [moment().add(-6, "months").toDate(), 26.99],
                                    [moment().add(-4, "months").toDate(), 11.99],
                                    [moment().add(-2, "months").toDate(), 21.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Years3:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-2, "years").toDate(), 17.99],
                                    [moment().add(-18, "months").toDate(), 21.99],
                                    [moment().add(-1, "years").toDate(), 18.99],
                                    [moment().add(-6, "months").toDate(), 28.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.Years5:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-4, "years").toDate(), 17.99],
                                    [moment().add(-3, "years").toDate(), 21.99],
                                    [moment().add(-2, "years").toDate(), 14.99],
                                    [moment().add(-1, "years").toDate(), 21.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                            case InstrumentPerformanceGraphTimePeriod.YearToDate:
                                testData.graphDataPoints[enumValue].push(
                                    [moment().add(-4, "months").toDate(), 16.99],
                                    [moment().add(-3, "months").toDate(), 24.99],
                                    [moment().add(-2, "months").toDate(), 13.99],
                                    [moment().add(-1, "months").toDate(), 28.99],
                                    [moment().toDate(), 19.99],
                                );
                                break;
                        }
                    }

                    this.setInstrumentPerformanceData({stockSymbol, data: testData});
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
    }
});